import { useMemo } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { ScalingReasonCode, StorageTypeFilter } from '@cast/types-stop';
import { makeIsEnabled } from '@cast/utils';

import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { apiClient } from '@cast/infrastructure-api';

const STORAGE_ACTIONS_QUERY_KEY = 'storage-actions';
export const STORAGE_ACTIONS_PAGE_SIZE = 50;

const getStorageActionsQueryKey = ({
  organizationId,
  clusterId,
  nodeName,
  pvNames,
  statuses,
  storageType,
  reasonCodes,
  startTime,
  endTime,
  pageSize,
}: {
  organizationId: string | undefined;
  clusterId?: string;
  nodeName?: string;
  pvNames?: string[];
  statuses?: string[];
  storageType?: StorageTypeFilter;
  reasonCodes?: ScalingReasonCode[];
  startTime: string | undefined;
  endTime: string | undefined;
  pageSize: number | undefined;
}) =>
  [
    STORAGE_ACTIONS_QUERY_KEY,
    organizationId,
    clusterId,
    nodeName,
    pvNames,
    statuses,
    storageType,
    reasonCodes,
    startTime,
    endTime,
    pageSize,
  ] as const;

type UseStorageActionsQueryArgs = {
  clusterId?: string;
  nodeName?: string;
  pvNames?: string[];
  statuses?: string[];
  storageType?: StorageTypeFilter;
  reasonCodes?: ScalingReasonCode[];
  startTime: string | undefined;
  endTime: string | undefined;
  enabled?: boolean;
  pageSize?: number;
};

const getStorageActionsQueryOptions = ({
  organizationId,
  clusterId,
  nodeName,
  pvNames,
  statuses,
  storageType,
  reasonCodes,
  startTime,
  endTime,
  enabled,
  pageSize,
}: UseStorageActionsQueryArgs & { organizationId: string | undefined }) =>
  infiniteQueryOptions({
    queryKey: getStorageActionsQueryKey({
      organizationId,
      clusterId,
      nodeName,
      pvNames,
      statuses,
      storageType,
      reasonCodes,
      startTime,
      endTime,
      pageSize,
    }),
    queryFn: async ({ pageParam }) => {
      const persistentVolumeNames = pvNames || [];

      const { data } = await apiClient.stop.listStorageActions({
        organizationId: organizationId!,
        clusterId: clusterId!,
        ...(nodeName && { filterNodeNames: [nodeName] }),
        ...(persistentVolumeNames.length && {
          filterPersistentVolumeNames: persistentVolumeNames,
        }),
        ...(statuses?.length && { filterStatuses: statuses }),
        ...(storageType && { filterStorageType: storageType }),
        ...(reasonCodes?.length && { filterReasonCodes: reasonCodes }),
        startTime,
        endTime,
        ...(pageSize && { pageLimit: String(pageSize) }),
        pageCursor: pageParam,
      });

      return data;
    },
    enabled: makeIsEnabled(enabled !== false, !!organizationId, !!clusterId),
    initialPageParam: '0',
    getNextPageParam: (lastPage) => {
      if (pageSize === undefined) {
        return undefined;
      }
      return lastPage.nextPageCursor && lastPage.items?.length === pageSize
        ? lastPage.nextPageCursor
        : undefined;
    },
  });

export const useStorageActionsQuery = ({
  clusterId,
  nodeName,
  pvNames,
  statuses,
  storageType,
  reasonCodes,
  startTime,
  endTime,
  enabled,
  pageSize,
}: UseStorageActionsQueryArgs) => {
  const organizationId = useCurrentOrganizationId();

  const { data, ...props } = useInfiniteQuery(
    getStorageActionsQueryOptions({
      organizationId,
      clusterId,
      nodeName,
      pvNames,
      statuses,
      storageType,
      reasonCodes,
      startTime,
      endTime,
      enabled,
      pageSize,
    })
  );

  const storageActions = useMemo(() => {
    return flatMap(data?.pages, (page) => page.items ?? []);
  }, [data]);

  return {
    ...props,
    storageActions,
    totalCount: data?.pages[0]?.totalCount ?? 0,
    storageActionsSummary: data?.pages[0]?.summary ?? {
      sizeIncreasedCount: 0,
      iopsIncreasedCount: 0,
      iopsDecreasedCount: 0,
      throughputIncreasedCount: 0,
      throughputDecreasedCount: 0,
    },
  };
};
